import { Grid, GridItem, Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBell, FaFolder, FaInfoCircle, FaShareAlt, FaTasks } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useGetContractQuery } from "../../../autogen/bff-api";
import { EntityViewSkeleton } from "../../../common/EntityViewSkeleton";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../common/errors/useApiError";
import { Layout } from "../../../common/layout/Layout";
import { ContractState } from "../../../common/redux/reducers/contractReducer";
import { ContractsTab, useQueryParams } from "../../../common/useQueryParams";
import { CONTENT_WIDTH } from "../../../config";
import { requireCounterparty, requireDataFields } from "../typeguards";
import { ContractMeta } from "./ContractMeta";
import { Details } from "./details/Details";
import { Documents } from "./documents/Documents";
import { Notifications } from "./notifications/Notifications";
import { Sharing } from "./sharing/Sharing";
import { Tasks } from "./tasks/Tasks";

const indexToQueryValue = (index: number): ContractsTab => {
  if (index === 0) return "info";
  else if (index === 1) return "documents";
  else if (index === 2) return "reminders";
  else if (index === 3) return "tasks";
  else if (index === 4) return "sharing";
  else throw Error("Invalid index");
};

const tabToIndex = (tab: ContractsTab): number => {
  switch (tab) {
    case "info":
      return 0;
    case "documents":
      return 1;
    case "reminders":
      return 2;
    case "tasks":
      return 3;
    case "sharing":
      return 4;
  }
};

export const ContractPage = () => {
  const { contractId } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const params = useQueryParams();
  const { t } = useTranslation();
  const { data: contract, error, isLoading } = useGetContractQuery(contractId ? { contractId } : skipToken);
  const displayer = useApiError();

  useEffect(() => {
    const tab = params.getContractsTab();
    setTabIndex(tabToIndex(tab));
  }, [params]);

  if (error) {
    if (isFetchBaseQueryError(error) && error.status === 403) return displayer.showNoAccess();
    else return displayer.show(error);
  }

  const endDate = contract ? requireDataFields(contract as ContractState["contract"]).endDate : undefined;

  return (
    <Layout>
      <Grid
        height="full"
        gap={1}
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"1fr"}
        templateAreas={`
                "leftContent content rightContent"
                `}
      >
        <GridItem area={"content"}>
          <Grid
            width="full"
            gridTemplateColumns={"3fr 4fr 200px"}
            gridTemplateRows="minmax(50px,1fr) auto auto auto minmax(0,1fr)"
            templateAreas={`
                        "title title edit"
                        "createdMeta createdMeta edit"
                        "deadline deadline deadline"
                        "content content content"
                        `}
            px="1"
          >
            {isLoading && <EntityViewSkeleton />}
            {contract && contract.id === contractId && (
              <>
                <ContractMeta
                  id={contract.id}
                  title={contract.title}
                  counterparty={requireCounterparty(contract)}
                  status={contract.status ?? undefined}
                  expirationDate={endDate?.date ?? null}
                  canEdit={contract.canEditEverything}
                  template={contract.template ?? null}
                />
                <GridItem area={"content"} display={"flex"} flexDirection={"column"}>
                  <Tabs
                    isLazy={true}
                    mt="25px"
                    width={"100%"}
                    index={tabIndex}
                    onChange={(index) => {
                      setTabIndex(index);
                      params.setSelectedContractsTab(indexToQueryValue(index));
                    }}
                    variant="line"
                  >
                    <TabList>
                      {contract.canViewDataFields && (
                        <Tab>
                          <Icon as={FaInfoCircle} w="15px" h="15px" mr="10px" />
                          {t("Details")}
                        </Tab>
                      )}
                      {contract.canViewDocuments && (
                        <Tab>
                          <Icon as={FaFolder} w="15px" h="15px" mr="10px" />
                          {t("Documents")}
                        </Tab>
                      )}
                      {contract.canViewNotifications && (
                        <Tab>
                          <Icon as={FaBell} w="15px" h="15px" mr="10px" />
                          {t("Reminders")}
                        </Tab>
                      )}
                      {contract.canViewTasks && (
                        <Tab>
                          <Icon as={FaTasks} w="15px" h="15px" mr="10px" />
                          {t("Tasks")}
                        </Tab>
                      )}
                      {contract.canViewParticipants && (
                        <Tab>
                          <Icon as={FaShareAlt} w="15px" h="15px" mr="10px" />
                          {t("Sharing")}
                        </Tab>
                      )}
                    </TabList>
                    <TabPanels>
                      {contract.canViewDataFields && (
                        <TabPanel p="0" pt="8">
                          <Details data={contract as ContractState["contract"]} />
                        </TabPanel>
                      )}
                      {contract.canViewDocuments && (
                        <TabPanel p="0" pt="8">
                          <Documents contract={contract} canEdit={contract.canEditEverything} />
                        </TabPanel>
                      )}
                      {contract.canViewNotifications && (
                        <TabPanel p="0" pt="8">
                          <Notifications data={contract} />
                        </TabPanel>
                      )}
                      {contract.canViewTasks && (
                        <TabPanel p="0" pt="8">
                          <Tasks contract={contract} />
                        </TabPanel>
                      )}
                      {contract.canViewParticipants && (
                        <TabPanel p="0" pt="8">
                          <Sharing contract={contract} />
                        </TabPanel>
                      )}
                    </TabPanels>
                  </Tabs>
                </GridItem>
              </>
            )}
          </Grid>
        </GridItem>
      </Grid>
    </Layout>
  );
};
